import { useState } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Mic2, Radio, Camera, Headphones, Building2, Plus, CheckCircle, Loader2, Music } from 'lucide-react';
import { API } from '../config/api';



const ROLE_CONFIG = {
  podcaster: {
    icon: Mic2,
    color: 'green',
    title: 'Podcaster',
    description: 'Create and share podcast episodes, interview DMV artists',
    endpoint: '/podcasters/profile',
    fields: [
      { name: 'show_name', label: 'Show Name', type: 'text', required: true, placeholder: 'Your podcast name' },
      { name: 'bio', label: 'Bio', type: 'textarea', placeholder: 'Tell us about your show...' },
      { name: 'category', label: 'Category', type: 'select', options: ['music', 'culture', 'interviews', 'news', 'general'] },
      { name: 'booking_link', label: 'Booking Link', type: 'text', placeholder: 'https://calendly.com/...' }
    ]
  },
  streamer: {
    icon: Radio,
    color: 'red',
    title: 'Streamer / Host',
    description: 'Host interviews, live sessions, and listening parties',
    endpoint: '/streamers/profile',
    fields: [
      { name: 'display_name', label: 'Display Name', type: 'text', required: true, placeholder: 'Your host name' },
      { name: 'bio', label: 'Bio', type: 'textarea', placeholder: 'Tell us about your streams...' },
      { name: 'stream_type', label: 'Primary Content', type: 'select', options: ['interviews', 'listening_sessions', 'panels', 'live_shows'] },
      { name: 'booking_link', label: 'Booking Link', type: 'text', placeholder: 'https://calendly.com/...' }
    ]
  },
  videographer: {
    icon: Camera,
    color: 'purple',
    title: 'Videographer',
    description: 'Create music videos, interviews, and visual content',
    endpoint: '/videographers/profile',
    fields: [
      { name: 'display_name', label: 'Display Name', type: 'text', required: true, placeholder: 'Your name or brand' },
      { name: 'bio', label: 'Bio', type: 'textarea', placeholder: 'Tell us about your work...' },
      { name: 'location', label: 'Location', type: 'text', placeholder: 'DC, Maryland, Virginia' },
      { name: 'portfolio_url', label: 'Portfolio URL', type: 'text', placeholder: 'https://yourportfolio.com' },
      { name: 'rate_range', label: 'Rate Range', type: 'text', placeholder: '$500-2000' },
      { name: 'booking_link', label: 'Booking Link', type: 'text', placeholder: 'https://calendly.com/...' }
    ]
  },
  engineer: {
    icon: Headphones,
    color: 'blue',
    title: 'Audio Engineer',
    description: 'Mix, master, and engineer tracks for DMV artists',
    endpoint: '/engineers/profile',
    fields: [
      { name: 'display_name', label: 'Display Name', type: 'text', required: true, placeholder: 'Your name or brand' },
      { name: 'bio', label: 'Bio', type: 'textarea', placeholder: 'Tell us about your expertise...' },
      { name: 'location', label: 'Location', type: 'text', placeholder: 'DC, Maryland, Virginia' },
      { name: 'rate_range', label: 'Rate Range', type: 'text', placeholder: '$50-200/hr' },
      { name: 'booking_link', label: 'Booking Link', type: 'text', placeholder: 'https://calendly.com/...' }
    ]
  },
  studio: {
    icon: Building2,
    color: 'amber',
    title: 'Recording Studio',
    description: 'List your studio for booking by DMV artists',
    endpoint: '/studios',
    fields: [
      { name: 'name', label: 'Studio Name', type: 'text', required: true, placeholder: 'Your studio name' },
      { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe your studio...' },
      { name: 'address', label: 'Address', type: 'text', required: true, placeholder: 'Full address' },
      { name: 'hourly_rate', label: 'Hourly Rate', type: 'number', placeholder: '50' },
      { name: 'booking_link', label: 'Booking Link', type: 'text', placeholder: 'https://calendly.com/...' }
    ]
  }
};

export function RoleApplicationSection({ user, getAuthHeaders, onRoleAdded }) {
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Get user's existing roles
  const userRoles = user?.roles || [];
  const isPodcaster = user?.is_podcaster || userRoles.includes('podcaster');
  const isStreamer = user?.is_streamer || userRoles.includes('streamer');
  const isVideographer = user?.is_videographer || userRoles.includes('videographer');
  const isEngineer = user?.is_engineer || userRoles.includes('engineer');
  const hasStudio = user?.has_studio;

  const roleStatus = {
    podcaster: isPodcaster,
    streamer: isStreamer,
    videographer: isVideographer,
    engineer: isEngineer,
    studio: hasStudio
  };

  const handleOpenDialog = (roleKey) => {
    setSelectedRole(roleKey);
    setFormData({});
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    const config = ROLE_CONFIG[selectedRole];
    
    // Validate required fields
    for (const field of config.fields) {
      if (field.required && !formData[field.name]) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}${config.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(`${config.title} profile created successfully!`);
        setDialogOpen(false);
        setFormData({});
        if (onRoleAdded) onRoleAdded(selectedRole);
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to create ${config.title} profile`);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="role-application-section">
      {/* Header */}
      <div>
        <h2 className="font-heading text-2xl font-bold uppercase mb-2">Expand Your Presence</h2>
        <p className="text-muted-foreground">
          Add additional roles to your profile and reach more of the DMV creative community.
        </p>
      </div>

      {/* Role Cards Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(ROLE_CONFIG).map(([roleKey, config]) => {
          const Icon = config.icon;
          const hasRole = roleStatus[roleKey];
          const colorClass = `${config.color}-500`;
          
          return (
            <Card 
              key={roleKey} 
              className={`glass-card border-${config.color}-500/30 p-4 ${hasRole ? 'opacity-60' : ''}`}
              data-testid={`role-card-${roleKey}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-lg bg-${config.color}-500/20 flex items-center justify-center`}>
                  <Icon className={`w-5 h-5 text-${config.color}-400`} />
                </div>
                {hasRole && (
                  <Badge className={`bg-${config.color}-500/20 text-${config.color}-400 border-${config.color}-500/30`}>
                    <CheckCircle className="w-3 h-3 mr-1" /> Active
                  </Badge>
                )}
              </div>
              <h3 className="font-bold mb-1">{config.title}</h3>
              <p className="text-sm text-muted-foreground mb-4">{config.description}</p>
              
              {hasRole ? (
                <Button variant="outline" size="sm" disabled className="w-full">
                  Already Applied
                </Button>
              ) : (
                <Dialog open={dialogOpen && selectedRole === roleKey} onOpenChange={(open) => {
                  if (!open) setDialogOpen(false);
                }}>
                  <DialogTrigger asChild>
                    <Button 
                      size="sm" 
                      className={`w-full bg-${config.color}-600 hover:bg-${config.color}-700`}
                      onClick={() => handleOpenDialog(roleKey)}
                      data-testid={`apply-${roleKey}-btn`}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Apply Now
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md bg-card border-border">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 text-${config.color}-400`} />
                        Apply as {config.title}
                      </DialogTitle>
                      <DialogDescription>
                        Fill out the form below to create your {config.title.toLowerCase()} profile.
                      </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                      {config.fields.map((field) => (
                        <div key={field.name} className="space-y-2">
                          <Label className="flex items-center gap-1">
                            {field.label}
                            {field.required && <span className="text-red-500">*</span>}
                          </Label>
                          
                          {field.type === 'textarea' ? (
                            <Textarea
                              value={formData[field.name] || ''}
                              onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                              placeholder={field.placeholder}
                              rows={3}
                            />
                          ) : field.type === 'select' ? (
                            <Select
                              value={formData[field.name] || ''}
                              onValueChange={(v) => setFormData({ ...formData, [field.name]: v })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                              </SelectTrigger>
                              <SelectContent>
                                {field.options.map((opt) => (
                                  <SelectItem key={opt} value={opt}>
                                    {opt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              type={field.type}
                              value={formData[field.name] || ''}
                              onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                              placeholder={field.placeholder}
                            />
                          )}
                        </div>
                      ))}
                      
                      <Button 
                        onClick={handleSubmit} 
                        disabled={submitting}
                        className={`w-full bg-${config.color}-600 hover:bg-${config.color}-700`}
                        data-testid="submit-role-application"
                      >
                        {submitting ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Create {config.title} Profile
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </Card>
          );
        })}
      </div>

      {/* Info Box */}
      <Card className="glass-card border-border p-4 bg-muted/20">
        <div className="flex items-start gap-3">
          <Music className="w-5 h-5 text-secondary mt-0.5" />
          <div>
            <h4 className="font-bold text-sm mb-1">One Account, Multiple Roles</h4>
            <p className="text-xs text-muted-foreground">
              Your DMV Soundstage account can hold multiple creative profiles. 
              Each role gives you access to different features and visibility on the platform.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
